// Cross-site nav data for the CPG cross-brand bar. Ported from
// Core/NextJS/apps/cpgjobs/app/components/Cpg/cpg-sites.ts (which mirrors
// BevNET/Core/Blazor/BevNET.BlazorLibrary/Cpg/CpgMediaSites.cs and the
// DbEnums.SiteUrl / DbEnums.SiteName tables). App-local copy for the Jobs SPA so
// the cross-brand switcher knows about every site; the Jobs bar filters cpgjobs
// out of its own list via getCpgNavLinks('cpgjobs').

export type CpgSite =
  | 'bevnet'
  | 'nosh'
  | 'brewbound'
  | 'tasteradio'
  | 'nombase'
  | 'cpgjobs';

export interface CpgSiteEntry {
  key: CpgSite;
  url: string;
  name: string;
}

// Env-aware base for the cpgjobs.com marketing/board site this SPA links OUT to.
// Prod = www.cpgjobs.com; dev/local = localhost:3106 (the cpgjobs Next.js app).
// REACT_APP_CPGJOBS_SITE_URL overrides both. Trailing slash trimmed for clean joins.
export const CPGJOBS_SITE_URL = (
  process.env.REACT_APP_CPGJOBS_SITE_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://www.cpgjobs.com'
    : 'http://localhost:3106')
).replace(/\/+$/, '');

// Order matches Blazor `CpgMediaSites.GetNavLinks(...)`: Bevnet, Nosh, Brewbound,
// TasteRadio, Nombase — with CPG Jobs appended (new service site).
const SITES: CpgSiteEntry[] = [
  { key: 'bevnet', url: 'https://www.bevnet.com', name: 'BevNET' },
  { key: 'nosh', url: 'https://www.nosh.com', name: 'Nosh' },
  { key: 'brewbound', url: 'https://www.brewbound.com', name: 'Brewbound' },
  { key: 'tasteradio', url: 'https://www.tasteradio.com', name: 'Taste Radio' },
  { key: 'nombase', url: 'https://www.nombase.com', name: 'Nombase' },
  { key: 'cpgjobs', url: CPGJOBS_SITE_URL, name: 'CPG Jobs' },
];

// Returns cross-site links with the current site filtered out. Pass null/
// undefined to get the full list.
export const getCpgNavLinks = (currentSite?: CpgSite | null): CpgSiteEntry[] =>
  SITES.filter((s) => s.key !== currentSite);

// Static brand assets referenced by the bar.
export const CPG_MEDIA_KIT_URL = 'https://www.cpgmedia.com/';
export const CPG_HEADER_LOGO_URL =
  'https://d2btcxja5g5zgs.cloudfront.net/assets/images/CPG-header-logo.svg';

// Header shortcut targets — the network-wide CPG Media pages.
export const CPG_SLACK_URL = 'https://www.cpgmedia.com/slack';
export const CPG_NEWSLETTER_URL = 'https://www.cpgmedia.com/newsletter';
export const CPG_PODCASTS_URL = 'https://www.cpgmedia.com/podcasts';
export const CPG_SEARCH_URL = 'https://search.cpgmedia.com';

export interface InsiderCtaCampaign {
  campaign: string;
  text: string;
}

export const INSIDER_CTA_DEFAULT: InsiderCtaCampaign = {
  campaign: 'zeta',
  text: 'Become an Insider',
};

// Same 50/50 A/B as SubscribeUrls.GetInsiderButtonCampaign(); client-side only.
export function getInsiderCtaCampaign(): InsiderCtaCampaign {
  return Math.random() < 0.5
    ? INSIDER_CTA_DEFAULT
    : { campaign: 'eta', text: 'See Insider Plans' };
}

// "Become an Insider" CTA target. utm_site identifies cpg-jobs as the referrer.
export function getInsiderCtaUrl(
  currentUrl?: string,
  campaign: string = INSIDER_CTA_DEFAULT.campaign,
): string {
  const params = new URLSearchParams();
  params.set('utm_site', 'cpg-jobs');
  if (currentUrl) params.set('utm_page', currentUrl);
  params.set('utm_source', 'insider_cta');
  params.set('utm_campaign', campaign);
  return `https://app.bevnet.com/subscribe?${params.toString()}`;
}

// "Manage Account" destination on the shared BevNET account portal. Mirrors
// Nombase's CPG_USER_ACCOUNT_URL — the logged-in account dropdown's account link.
export const CPG_USER_ACCOUNT_URL = 'https://account.bevnet.com/user/account';

// Jobs management remains in this SPA rather than the shared account portal.
export const CPG_JOBS_APP_URL = (
  process.env.REACT_APP_CPGJOBS_APP_URL || 'https://app.cpgjobs.com'
).replace(/\/+$/, '');
export const CPG_JOBS_ACCOUNT_URL = `${CPG_JOBS_APP_URL}/user/employer/listings`;
